import React from "react";
import { Box, Inline, Text, MenuRenderer, MenuItem, IconChevron, Drawer, IconOverflow, Stack, MenuItemDivider, IconTick } from "braid-design-system";
import { useTranslation } from 'react-i18next';
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import { useUserPermissionAndMenu, UserMenu } from "../contexts/UserPermissionAndMenuProvider";
import { compareOrderNum } from "../utils/menuSorter";
import CashierSignOnBadge from "./CashierSignOnBadge";
import "../styles/MenuBar.css";

// Type definition for menu items
interface MenuItemType {
    id?: number | null;
    label: string;
    to: string;
    children?: MenuItemType[];
    isDivider?: boolean;
}

/**
 * Build the nav tree from the current user's granted menus (it_user_menu, via
 * /users/menu_list).
 *
 * A granted child whose parent was NOT granted is still shown under that parent:
 * the parent (and any further ancestors) are pulled from the full menu catalog
 * purely as grouping containers. Only granted leaves appear - ungranted siblings
 * of the same parent stay hidden. Menus the user has no grant for are absent.
 */
const buildMenuTree = (granted: UserMenu[], catalog: UserMenu[]): MenuItemType[] => {
    const grantedVisible = granted.filter(m => m.has_menu !== false && m.id != null);
    const catalogById = new Map<number, UserMenu>();
    catalog.forEach(m => { if (m.id != null) catalogById.set(m.id, m); });

    // Every menu that must appear: granted leaves + their ancestor chain.
    const included = new Map<number, UserMenu>();
    grantedVisible.forEach(m => {
        included.set(m.id as number, m);
        let parentId = m.parent_id ?? null;
        while (parentId != null && !included.has(parentId)) {
            const ancestor = catalogById.get(parentId);
            if (!ancestor) break;
            included.set(parentId, ancestor);
            parentId = ancestor.parent_id ?? null;
        }
    });

    const nodes = Array.from(included.values());
    const includedIds = new Set(nodes.map(n => n.id as number));

    const toNode = (menu: UserMenu): MenuItemType => {
        const children = nodes
            .filter(child => child.parent_id === menu.id)
            .sort((a, b) => compareOrderNum(a.order_num, b.order_num))
            .map(toNode);
        return {
            id: menu.id,
            label: menu.label || "",
            to: menu.to_url || "/",
            children: children.length > 0 ? children : undefined,
        };
    };

    return nodes
        .filter(menu => menu.parent_id == null || !includedIds.has(menu.parent_id))
        .sort((a, b) => compareOrderNum(a.order_num, b.order_num))
        .map(toNode);
};

const getMenuPath = (menuPath: string): string => (menuPath || "").split("?")[0] || menuPath;

const getCurrentMenuUrl = (pathname: string, search: string): string => `${pathname}${search || ""}`;

const isMenuActive = (pathname: string, menuPath: string): boolean => {
    const normalizedMenuPath = getMenuPath(menuPath);

    if (!normalizedMenuPath || normalizedMenuPath === "/") {
        return pathname === "/";
    }

    return pathname === normalizedMenuPath || pathname.startsWith(`${normalizedMenuPath}/`);
};

const isMenuSelected = (pathname: string, search: string, menuPath: string): boolean => {
    const currentUrl = getCurrentMenuUrl(pathname, search);
    const normalizedMenuPath = menuPath || "";

    if (normalizedMenuPath.includes("?")) {
        return currentUrl === normalizedMenuPath;
    }

    return getMenuPath(pathname) === getMenuPath(normalizedMenuPath);
};

const MenuBar: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { userMenus, systemMenus } = useUserPermissionAndMenu();
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const menuItems = React.useMemo(
        () => buildMenuTree(userMenus || [], systemMenus || []),
        [userMenus, systemMenus],
    );

    const handleMenuItemClick = (to: string) => {
        navigate(to);
        setDrawerOpen(false);
    };

    const renderMenuItemsForDrawer = (items: MenuItemType[]) => {
        return items.map((item, index) => (
            item.children ? (
                <MenuRenderer
                    key={`drawer-${item.to || item.label}-${index}`}
                    width="large"
                    trigger={(triggerProps, { open }) => (
                        <Box
                            paddingY="small"
                            paddingX="medium"
                            display="flex"
                            alignItems="center"
                            cursor="pointer"
                            {...triggerProps}
                        >
                            <Text
                                tone={isMenuActive(location.pathname, item.to) ? "brandAccent" : "secondary"}
                                weight={isMenuActive(location.pathname, item.to) ? "strong" : "regular"}

                            >
                                {t(
                                    `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                    { defaultValue: item.label }
                                )}
                                <IconChevron direction={open ? "up" : "down"} />
                            </Text>
                        </Box>
                    )}
                >
                    {item.children.map((child, childIndex) => (
                        child.isDivider ? (
                            <MenuItemDivider key={`drawer-divider-${childIndex}`} />
                        ) : child.children ? (
                            // Child item with nested children (like report categories)
                            <MenuRenderer
                                key={`drawer-nested-${child.to}-${childIndex}`}
                                width="large"
                                trigger={(triggerProps, { open }) => (
                                    <Box
                                        paddingX="small"
                                        paddingY="xxsmall"
                                        display="flex"
                                        alignItems="center"
                                        cursor="pointer"
                                        {...triggerProps}
                                    >
                                        <Text size="small">
                                            {t(
                                                `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                { defaultValue: child.label }
                                            )}
                                            <IconChevron direction={open ? "up" : "down"} />
                                        </Text>
                                    </Box>
                                )}
                            >
                                {child.children.map((report, reportIndex) => (
                                    <MenuItem
                                        key={`drawer-report-${report.to}-${reportIndex}`}
                                        onClick={() => handleMenuItemClick(report.to)}
                                        icon={location.pathname === report.to ? <IconTick /> : undefined}
                                    >
                                        {t(
                                            `menu.${(report.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                            { defaultValue: report.label }
                                        )}
                                    </MenuItem>
                                ))}
                            </MenuRenderer>
                        ) : (
                            <MenuItem
                                key={`drawer-child-${child.to}-${childIndex}`}
                                onClick={() => handleMenuItemClick(child.to)}
                                icon={isMenuSelected(location.pathname, location.search, child.to) ? <IconTick /> : undefined}
                            >
                                {t(
                                    `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                    { defaultValue: child.label }
                                )}
                            </MenuItem>
                        )
                    ))}
                </MenuRenderer>
            ) : (
                <Box
                    key={`drawer-${item.to}-${index}`}
                    paddingY="small"
                    paddingX="medium"
                    cursor="pointer"
                    onClick={() => handleMenuItemClick(item.to)}
                >
                    <RouterLink to={item.to} style={{ textDecoration: "none" }}>
                        <Text
                            tone={location.pathname === item.to ? "brandAccent" : "secondary"}
                            weight={location.pathname === item.to ? "strong" : "regular"}
                        >
                            {t(
                                `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                { defaultValue: item.label }
                            )}
                        </Text>
                    </RouterLink>
                </Box>
            )
        ));
    };

    return (
        <Box background="brand" paddingY="xxsmall" paddingX="xsmall" paddingLeft={"large"}>
            <style>{`
                @media (max-width: 1215px) {
                    .menu-drawer-trigger { display: block; }
                    .menu-horizontal { display: none; }
                }
                @media (min-width: 1216px) {
                    .menu-drawer-trigger { display: none; }
                    .menu-horizontal { display: block; }
                }
            `}</style>

            <div className="menu-drawer-trigger">
                <Inline space="xsmall" alignY="center">
                    <Box
                        paddingY="xxsmall"
                        cursor="pointer"
                        onClick={() => setDrawerOpen(true)}
                    >
                        <IconOverflow tone="secondary" />
                    </Box>
                    <Box flexGrow={1} />
                </Inline>

                <Drawer
                    title="Menu"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                >
                    <Stack space="none">
                        {renderMenuItemsForDrawer(menuItems)}
                    </Stack>
                </Drawer>
            </div>

            <div className="menu-horizontal">
                <Inline space="xsmall" alignY="center">
                    {menuItems.map((item, index) =>
                        item.children ? (
                            <MenuRenderer
                                key={`${item.to}-${index}`}
                                width="large"
                                trigger={(triggerProps, { open }) => (
                                    <Box
                                        paddingY={"xxsmall"}
                                        className={`menu-link ${isMenuActive(location.pathname, item.to) ? "menu-active" : ""}`}
                                        display="flex"
                                        alignItems="center"
                                        cursor="pointer"
                                        {...triggerProps}
                                    >
                                        <Text
                                            weight={isMenuActive(location.pathname, item.to) ? "strong" : "regular"}
                                            size="small"
                                        >
                                            {t(
                                                `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                { defaultValue: item.label }
                                            )}
                                            <IconChevron direction={open ? "up" : "down"} />
                                        </Text>
                                    </Box>
                                )}
                            >
                                {item.children.map((child, childIndex) => (
                                    child.isDivider ? (
                                        <MenuItemDivider key={`divider-${childIndex}`} />
                                    ) : child.children ? (
                                        <MenuRenderer
                                            key={`${child.to}-${childIndex}`}
                                            width="large"
                                            trigger={(triggerProps, { open }) => (
                                                <Box
                                                    paddingX="small"
                                                    paddingY="xxsmall"
                                                    display="flex"
                                                    alignItems="center"
                                                    cursor="pointer"
                                                    {...triggerProps}
                                                >
                                                    <Text size="small">
                                                        {t(
                                                            `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                            { defaultValue: child.label }
                                                        )}
                                                        <IconChevron direction={open ? "up" : "down"} />
                                                    </Text>
                                                </Box>
                                            )}
                                        >
                                            {child.children.map((report, reportIndex) => (
                                                <MenuItem
                                                    key={`${report.to}-${reportIndex}`}
                                                    onClick={() => navigate(report.to)}
                                                    icon={isMenuSelected(location.pathname, location.search, report.to) ? <IconTick /> : undefined}
                                                >
                                                    {t(
                                                        `menu.${(report.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                        { defaultValue: report.label }
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </MenuRenderer>
                                    ) : (
                                        <MenuItem
                                            key={`${child.to}-${childIndex}`}
                                            onClick={() => navigate(child.to)}
                                            icon={isMenuSelected(location.pathname, location.search, child.to) ? <IconTick /> : undefined}
                                        >
                                            {t(
                                                `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                { defaultValue: child.label }
                                            )}
                                        </MenuItem>
                                    )
                                ))}
                            </MenuRenderer>
                        ) : (
                            <Box
                                key={`${item.to}-${index}`}
                                paddingY={"xxsmall"}
                                className={`menu-link ${isMenuActive(location.pathname, item.to) ? "menu-active" : ""}`}
                            >
                                <RouterLink to={item.to} style={{ textDecoration: "none" }}>
                                    <Text
                                        weight={isMenuActive(location.pathname, item.to) ? "strong" : "regular"}
                                        size="small"
                                    >
                                        {t(
                                            `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                            { defaultValue: item.label }
                                        )}
                                    </Text>
                                </RouterLink>
                            </Box>
                        )
                    )}
                    <Box flexGrow={1} />
                    <CashierSignOnBadge />

                </Inline>
            </div>
        </Box>
    );
};

export default MenuBar;
